import React from 'react';
import styles from "./EntryPageContent.module.css";
import { Container, Row, Col, Accordion, AccordionContext, useAccordionToggle } from 'react-bootstrap';
import ArticleElement from '../../../components/ArticleElement/ArticleElement';
import SocialMediaShare from '../../../components/SocialMediaShare/SocialMediaShare';
import {getShareURL, selectImageUrl} from '../../../helpers/helpers'

const Quotes = ({quotes}) => {
    return (
        <>
            <h2 className={styles.QuotesHeadline}>Comment from D&AD Community</h2>
            <ul className={styles.Quotes}>
                {quotes.map((quote, i) => (
                    <li key={`quote-${i}`}>
                        <blockquote>
                            <p>{quote.copy}</p>
                            <cite>— {quote.author}, {quote.jobTitle}, {quote.company}</cite>
                        </blockquote>
                    </li>
                ))}
            </ul>
        </>
    )
}

const CreditsToggle = ({eventKey, callback}) => {
    const currentEventKey = React.useContext(AccordionContext);
    const decoratedOnClick = useAccordionToggle(
        eventKey,
        () => callback && callback(eventKey),
    );
    const isCurrentEventKey = currentEventKey === eventKey;
    const cta = isCurrentEventKey ? "hide credits" : "show all credits"
    return (
        <Accordion.Toggle variant="link" className={[styles.showAllCredits, isCurrentEventKey ? styles.showAllCreditsOpen : ""].join(" ")} eventKey={eventKey} onClick={decoratedOnClick}>
            {cta}
        </Accordion.Toggle>
    )
}

const CreditList = (credit, k) => {
    return (
        <div key={`creditlist-${k}`}>
            <h3 className={styles.creditRole}>{credit.title}</h3>
            <ul>
                {credit.names.map((name, i) => (
                    <li key={`${credit.title}-${i}`}>{name}</li>
                ))}
            </ul>
        </div>
    )
}

const Credits = ({credits}) => {
    return (
        <div className={styles.Credits}>
            <div className={[styles.CreditBox, styles.FirstCreditBox].join(" ")}>
                {credits.slice(0,3).map((credit, i) => (CreditList(credit,i)))}
            </div>
            {credits.slice(3).length > 0 &&
                <Accordion className={styles.Accordion}>
                    <Accordion.Collapse eventKey="0" className={styles.AccordionCollapse}>
                        <div className={styles.CreditBox} style={{marginTop: "1rem"}}>
                            {credits.slice(3).map((credit, i) => (CreditList(credit, i)))}
                        </div>
                    </Accordion.Collapse>
                    <CreditsToggle eventKey="0"/>
                </Accordion>
            }
        </div>
    )
}

const pencilMap = {
    'black-pencil': styles.PencilBlack,
    'yellow-pencil': styles.PencilYellow,
    'graphite-pencil': styles.PencilGraphite,
    'wood-pencil': styles.PencilWood,
    'white-pencil': styles.PencilWhite,
    'shortlist': styles.Shortlist,
    'future-impact-pencil': styles.PencilWood,
    'next-pencil': styles.PencilWood,
    'side-hustle-pencil': styles.PencilWood,
    'collaborative-pencil': styles.PencilCollaborative,
    'future-impact-pencil': styles.PencilWood
}

const Awards = ({awards}) => {
    const uniqueAwards = Object.values(awards.reduce((target, current) => {
        const awardKey = current.jury ? `JURY: ${current.jury}, CAT: ${current.juryCategory}` : current.brief
        target[awardKey] = target[awardKey] || current
        return target
    }, {}))
    const [hover, setHover] = React.useState(false)

    const handleHover = (hoverState) => {
        setHover(hoverState)
    }

    return (
        <div className={styles.AwardsWrapper}>
            <ul className={[styles.Awards, hover ? styles.AwardsHover : ""].join(" ")}>
                {uniqueAwards.sort((a, b) => a.position > b.position ? 1 : -1).map((award, i) => {
                    if (award.awardSlug in pencilMap) {
                        return (
                            <li
                                key={`${award.slug}-${i}`}
                                onMouseEnter={() => handleHover(true)}
                                onMouseLeave={() => handleHover(false)}
                                className={pencilMap[award.awardSlug]}
                            >
                                <div className={styles.pencilWrapper}></div>
                                {award.jury &&
                                        <div className={styles.pencilInfo}>{award.jury}<span className={styles.AwardsSubcategory}>{award.juryCategory}</span></div>
                                    }
                                    {award.brief &&
                                        <div className={styles.pencilInfo}>{award.brief}</div>
                                    }
                            </li>
                        )
                    } else {
                        return null
                    }
                })}
            </ul>
        </div>
    )
}

const AssetList = ({assets}) => {
    const selectAssetEntry = (asset, label) => {
        return asset.entries.find(entry => entry.label === label)
    }

    return (
        <ul className={styles.AssetList}>
            {assets.map((asset, i) => {
                const smallAssetEntry = selectAssetEntry(asset, 'small')
                const largeAssetEntry = selectAssetEntry(asset, 'large')

                return (
                    <li className={smallAssetEntry.type} key={`${smallAssetEntry.url} ${i}`}>
                        {smallAssetEntry.type === "image" &&
                        <picture>
                            <source media="(min-width:768px)" srcSet={largeAssetEntry.imageUrl}/>
                            <img src={smallAssetEntry.imageUrl} alt={smallAssetEntry.imageText}/>
                        </picture>
                        }
                        {smallAssetEntry.type === "video" &&
                        <video controls poster={largeAssetEntry.imageUrl}>
                            <source src={largeAssetEntry.url} controlsList="nodownload" type="video/mp4"/>
                        </video>
                        }
                    </li>
                )
            })}
        </ul>
    )
}

const EntryPageContent = ({showYear = false, entry, awardType}) => {
    let credits = []
    if (entry.agency_name) {
        credits.push({
            title: "Agency",
            names: [entry.agency]
        })
    }
    if (entry.client_name && entry.year !== "2020") {
        credits.push({
            title: "Client",
            names: [entry.client]
        })
    }
    if (entry.country) {
        credits.push({
            title: "Country",
            names: [entry.country]
        })
    }
    credits = [...credits, ...entry.credits]
    if (showYear ||
        entry.year !== 2020 && entry.awards.find(a => a.awardSlug === 'black-pencil') !== undefined ||
        // Hack to show year on Future Impact entries
        entry.awards.find(a => a.awardSlug === 'future-impact-pencil') !== undefined ||
        entry.awards.find(a => a.jury === 'Future Impact') !== undefined ||
        entry.year === 2019 && (entry.awards.find(a => a.jury === 'Impact') || entry.awards.find(a => a.jury === 'Future') )
    ) {
        credits.unshift({
            title: "Year",
            names: [entry.year]
        })
    }
    return (
        <article className={styles.EntryPageContent}>
            <header>
                <picture>
                    <source media="(min-width:768px)" srcSet={selectImageUrl(entry.images, 'large')} />
                    <img src={selectImageUrl(entry.images, 'small')} alt={entry.title} />
                </picture>
            </header>
            <SocialMediaShare url={getShareURL('entry', entry.award, entry.id)} />
            <Container className={styles.entryPageMain}>
                <Row>
                    <Col md={9} className={styles.EntryInfo}>
                        <h1>{entry.title}</h1>
                        <p className="d-none d-md-block">{entry.description}</p>
                    </Col>
                    <Col md={3}>
                        <Awards awards={entry.awards} />
                        <Credits credits={credits} />
                    </Col>
                    <Col md={9} className={["d-md-none", styles.EntryPageDescription].join(" ")}>
                        <p>{entry.description}</p>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <AssetList assets={entry.assets}/>
                        {entry.quotes && entry.quotes.length > 0 && <Quotes quotes={entry.quotes} />}
                    </Col>
                </Row>
            </Container>
            {entry.relatedEditorial && <ArticleElement
                type="related"
                item={entry.relatedEditorial}
            />}
        </article>
    )
}

export default EntryPageContent